// src/store/builderStore.js
import { create } from "zustand";

function generateId() {
  return `${Date.now()}-${Math.floor(Math.random() * 100000)}-${cryptoRandom()}`;
}
function cryptoRandom() {
  try {
    // Works in browser and Node without relying on "globalThis"
    const g = typeof window !== "undefined"
      ? window
      : (typeof global !== "undefined" ? global : {});
    const buf = new Uint32Array(1);
    g.crypto?.getRandomValues?.(buf);
    return ((buf && buf[0]) || Math.floor(Math.random() * 1e9)).toString(36);
  } catch {
    return Math.floor(Math.random() * 1e9).toString(36);
  }
}

function buildDefaultState() {
  const meta = {
    title: "Untitled Template",
    description: "",
    logo: null,
    // access left undefined here—TemplateBuilder supplies "all" default visually
  };

  const sections = [
    {
      id: generateId(),
      title: "Title Page",
      description:
        "The Title Page is the first page of your inspection report. This section typically includes basic details.",
      repeatSection: false,
      questions: [
        { id: generateId(), label: "Site conducted", type: "site", required: true },
        { id: generateId(), label: "Conducted on", type: "date", required: false },
        { id: generateId(), label: "Prepared by", type: "person", required: false },
        { id: generateId(), label: "Location", type: "location", required: false },
      ],
    },
    {
      id: generateId(),
      title: "Untitled Page",
      description: "Add sections and questions for this page of your inspection.",
      repeatSection: false,
      questions: [
        {
          id: generateId(),
          label: "Untitled Question",
          type: "short_text",
          required: false,
          multipleSelection: false,
          flaggedResponses: false,
          linkResponse: false,
          logicEnabled: false,
        },
      ],
    },
  ];

  // MIGRATED STRUCTURE: sections now contain items[], not questions[]
  return {
    meta,
    sections: sections.map((sec) => ({
      id: sec.id,
      type: "section",
      title: sec.title,
      description: sec.description,
      repeatSection: sec.repeatSection || false,
      items: sec.questions.map((q) =>
        q.type === "section"
          ? {
              id: q.id,
              type: "section",
              title: q.label || "Untitled Section",
              description: "",
              repeatSection: q.repeatSection || false,
              items: []
            }
          : {
              id: q.id,
              type: "question",
              question: { ...q }
            }
      )
    }))
  };
}

// (Removed old constants; now using buildDefaultState() declared above)
const initial = buildDefaultState();

// ============================
// MIGRATION HELPERS (OPTION 1)
// ============================

// Convert old section { id, title, description, questions[] } → new { items: [] }
function migrateOldSection(oldSec) {
  return {
    id: String(oldSec.id),
    type: "section",
    title: oldSec.title || "Untitled Section",
    description: oldSec.description || "",
    repeatSection: oldSec.repeatSection || false,
    items: (oldSec.questions || []).map((q) =>
      q.type === "section"
        ? {
            id: String(q.id),
            type: "section",
            title: q.label || "Untitled Section",
            description: "",
            repeatSection: q.repeatSection || false,
            items: []
          }
        : {
            id: String(q.id),
            type: "question",
            question: { ...q }
          }
    )
  };
}

// Convert API payload into new structure safely
function migrateLoadedSections(sections) {
  if (!Array.isArray(sections)) return [];
  return sections.map((sec) => migrateOldSection(sec));
}

// Add an item (question or nested section) into a section by ID
function addItemToSection(sections, sectionId, item) {
  return sections.map((sec) => {
    if (sec.id === sectionId) {
      return {
        ...sec,
        items: [...(sec.items || []), item],
      };
    }
    if (sec.items?.length) {
      return {
        ...sec,
        items: addItemToSection(sec.items, sectionId, item),
      };
    }
    return sec;
  });
}

// ======================================================
// ITEM MANIPULATION HELPERS (replace, delete, reorder, move)
// ======================================================

// Recursively update a question item inside the tree
function updateQuestionInTree(items, questionId, changes) {
  return items.map((item) => {
    if (item.type === "question" && item.id === questionId) {
      return {
        ...item,
        question: { ...item.question, ...changes },
      };
    }
    if (item.type === "section" && item.items?.length) {
      return {
        ...item,
        items: updateQuestionInTree(item.items, questionId, changes),
      };
    }
    return item;
  });
}

// Recursively delete a question from the tree
function deleteQuestionInTree(items, questionId) {
  return items
    .filter((item) => item.id !== questionId)
    .map((item) =>
      item.type === "section"
        ? { ...item, items: deleteQuestionInTree(item.items, questionId) }
        : item
    );
}

// Recursively duplicate a question
function duplicateQuestionInTree(items, questionId) {
  const result = [];
  for (const item of items) {
    result.push(item);
    if (item.type === "question" && item.id === questionId) {
      const dup = {
        id: generateId(),
        type: "question",
        question: {
          ...item.question,
          id: generateId(),
          label: `${item.question.label || "Untitled Question"} (Copy)`,
        },
      };
      result.push(dup);
    }
    if (item.type === "section") {
      result[result.length - 1] = {
        ...item,
        items: duplicateQuestionInTree(item.items, questionId),
      };
    }
  }
  return result;
}

// Reorder items inside any section
function reorderItemsInTree(items, sectionId, newItems) {
  return items.map((item) => {
    if (item.id === sectionId && item.type === "section") {
      return {
        ...item,
        items: newItems,
      };
    }
    if (item.type === "section" && item.items?.length) {
      return {
        ...item,
        items: reorderItemsInTree(item.items, sectionId, newItems),
      };
    }
    return item;
  });
}

// Move an item between ANY two sections at ANY depth
function moveItemInTree(items, fromSectionId, toSectionId, sourceIndex, destIndex) {
  let movedItem = null;

  function removeItem(items) {
    return items.flatMap((item, idx) => {
      if (item.id === fromSectionId && item.type === "section") {
        movedItem = item.items[sourceIndex];
        const updated = item.items.filter((_, i) => i !== sourceIndex);
        return [{ ...item, items: updated }];
      }
      if (item.type === "section") {
        return [{ ...item, items: removeItem(item.items) }];
      }
      return [item];
    });
  }

  function insertItem(items) {
    return items.map((item) => {
      if (item.id === toSectionId && item.type === "section") {
        const updated = [...item.items];
        const pos = Math.min(destIndex, updated.length);
        updated.splice(pos, 0, movedItem);
        return { ...item, items: updated };
      }
      if (item.type === "section") {
        return { ...item, items: insertItem(item.items) };
      }
      return item;
    });
  }

  const without = removeItem(items);
  return insertItem(without);
}

// Flatten the entire structure into a linear array of { id, type, parentId, depth, item }
function flattenTree(sections) {
  const out = [];
  function walk(items, parentId = null, depth = 0) {
    for (const item of items) {
      out.push({
        id: String(item.id),
        type: item.type,
        parentId,
        depth,
        item,
      });
      if (item.type === "section" && Array.isArray(item.items)) {
        walk(item.items, String(item.id), depth + 1);
      }
    }
  }
  walk(sections, null, 0);
  return out;
}

// Insert a new item after a given index inside the correct parent array
function insertAfterIndex(sections, parentId, index, newItem) {
  function walk(items, parentIdToMatch) {
    if (parentIdToMatch === null) {
      // top-level insertion (pages array)
      const updated = [...items];
      updated.splice(index + 1, 0, newItem);
      return updated;
    }
    return items.map((item) => {
      if (item.id === parentIdToMatch && item.type === "section") {
        const updated = [...item.items];
        updated.splice(index + 1, 0, newItem);
        return { ...item, items: updated };
      }
      if (item.type === "section" && item.items?.length) {
        return { ...item, items: walk(item.items, parentIdToMatch) };
      }
      return item;
    });
  }
  return walk(sections, parentId);
}

export const useBuilderStore = create((set, get) => ({
  meta: initial.meta,
  sections: initial.sections,
  history: [], // <- now stores snapshots of { meta, sections }
  future: [],

  // UI-only:
  activeQuestion: null, // which question row is expanded
  collapsedSections: {}, // { [sectionId]: true/false }

  // --- SECTION LOOKUP --------------------------------------------------------
  getSectionById: (sectionId, sections = get().sections) => {
    sectionId = String(sectionId);
    function find(items) {
      for (const item of items) {
        if (item.id === sectionId && item.type === "section") return item;
        if (item.type === "section" && Array.isArray(item.items)) {
          const found = find(item.items);
          if (found) return found;
        }
      }
      return null;
    }
    return find(sections);
  },

  // --- DESCENDANT COLLECTION -------------------------------------------------
  collectDescendantItemIds: (node) => {
    const arr = [];
    function walk(item) {
      arr.push(String(item.id));
      if (item.type === "section" && Array.isArray(item.items)) {
        for (const child of item.items) walk(child);
      }
    }
    walk(node);
    return arr;
  },

  // --- Selection System (Model B: SC multi-select ready) ---
  activeRowId: null,           // currently focused question row
  activeSectionId: null,       // currently focused section header
  selectedItemIds: [],         // multi-select–friendly list of selected items

  // Collapse helpers (C2-A)
  toggleSectionCollapse: (sectionId) =>
    set((state) => {
      const isCollapsed = !!state.collapsedSections?.[sectionId];
      const newCollapsed = {
        ...state.collapsedSections,
        [sectionId]: !isCollapsed,
      };

      // SC behavior:
      // collapsing → header-only highlight
      // expanding → full block highlight
      if (isCollapsed) {
        // was collapsed → now expanding
        const sec = get().getSectionById(sectionId);
        if (sec) {
          const all = get().collectDescendantItemIds(sec);
          return {
            collapsedSections: newCollapsed,
            activeSectionId: String(sectionId),
            activeRowId: null,
            selectedItemIds: all,
          };
        }
      } else {
        // was expanded → collapsing now
        return {
          collapsedSections: newCollapsed,
          activeSectionId: String(sectionId),
          activeRowId: null,
          selectedItemIds: [String(sectionId)],
        };
      }

      return { collapsedSections: newCollapsed };
    }),

  // --- Reset builder state to new default (fresh IDs every time)
  resetStore: () =>
    set(() => {
      const fresh = buildDefaultState();
      return {
        meta: fresh.meta,
        sections: fresh.sections,
        history: [],
        future: [],
        activeQuestion: null,
      };
    }),
  setActiveQuestion: (sectionId, questionId) =>
    set(() => ({
      activeQuestion:
        sectionId && questionId ? { sectionId, questionId } : null,
    })),
  clearActiveQuestion: () =>
    set(() => ({
      activeQuestion: null,
    })),

  // --- SELECTION ACTIONS (Model B) -----------------------------------------
  setActiveRow: (rowId) => {
    set(() => ({
      activeRowId: String(rowId),
      activeSectionId: null,
      selectedItemIds: [String(rowId)],
    }));
  },

  setActiveSection: (sectionId) => {
    set(() => ({
      activeSectionId: String(sectionId),
      activeRowId: null,
      selectedItemIds: [String(sectionId)],
    }));
  },

  selectSectionAndChildren: (sectionId) => {
    const sec = get().getSectionById(sectionId);
    if (!sec) return;
    const allIds = get().collectDescendantItemIds(sec);
    set(() => ({
      activeSectionId: String(sectionId),
      activeRowId: null,
      selectedItemIds: allIds,
    }));
  },

  clearSelection: () =>
    set(() => ({
      activeRowId: null,
      activeSectionId: null,
      selectedItemIds: [],
    })),

  // --- Set all sections at once (for loading a template)
  setSections: (sections) =>
    set((state) => ({
      ...pushToHistory(state),
      sections: migrateLoadedSections(sections),
    })),

  // --- Meta actions (push full snapshot so undo/redo restores meta too)
  setMeta: (meta) =>
    set((state) => ({
      ...pushToHistory(state),
      meta: { ...state.meta, ...meta },
    })),

  // SECTION ACTIONS (top-level cards act as Pages in the UI)
  addSection: () => {
    const newSection = {
      id: generateId(),
      type: "section",
      title: "Untitled Page",
      description: "Add sections and questions for this page of your inspection.",
      repeatSection: false,
      items: [],
    };
    set((state) => {
      const sections = [...state.sections, newSection];
      return { ...pushToHistory(state), sections };
    });
  },

  duplicateSection: (sectionId) => {
    set((state) => {
      const idx = state.sections.findIndex((s) => s.id === sectionId);
      if (idx === -1) return {};
      const src = state.sections[idx];

      const deepCopyItems = (items) =>
        items.map((item) =>
          item.type === "question"
            ? {
                id: generateId(),
                type: "question",
                question: { ...item.question, id: generateId() },
              }
            : {
                id: generateId(),
                type: "section",
                title: item.title,
                description: item.description || "",
                repeatSection: item.repeatSection || false,
                items: deepCopyItems(item.items || []),
              }
        );

      const copy = {
        ...src,
        id: generateId(),
        title: src.title ? `${src.title} (Copy)` : "Untitled Section (Copy)",
        items: deepCopyItems(src.items || []),
      };
      const sections = [...state.sections];
      sections.splice(idx + 1, 0, copy);
      return { ...pushToHistory(state), sections };
    });
  },

  renameSection: (sectionId, newTitle) => {
    set((state) => {
      const sections = state.sections.map((s) =>
        s.id === sectionId ? { ...s, title: newTitle } : s
      );
      return { ...pushToHistory(state), sections };
    });
  },

  deleteSection: (sectionId) => {
    set((state) => {
      const sections = state.sections.filter((s) => s.id !== sectionId);
      return { ...pushToHistory(state), sections };
    });
  },

  reorderSections: (newSections) => {
    set((state) => ({
      ...pushToHistory(state),
      sections: [...newSections],
    }));
  },

  setSectionDescription: (sectionId, newDesc) => {
    set((state) => {
      const sections = state.sections.map((s) =>
        s.id === sectionId ? { ...s, description: newDesc } : s
      );
      return { ...pushToHistory(state), sections };
    });
  },

  toggleSectionRepeat: (sectionId) => {
    set((state) => {
      const sections = state.sections.map((s) =>
        s.id === sectionId
          ? { ...s, repeatSection: !s.repeatSection }
          : s
      );
      return { ...pushToHistory(state), sections };
    });
  },

  // Add a new Page at the end (top-level section)
  addPageAtEnd: () => {
    const state = get();
    const newPage = {
      id: generateId(),
      type: "section",
      title: "Untitled Page",
      description: "",
      repeatSection: false,
      items: [],
    };
    const updated = [...state.sections, newPage];
    set({
      ...pushToHistory(state),
      sections: updated,
      activeSectionId: String(newPage.id),
      activeRowId: null,
      selectedItemIds: [String(newPage.id)],
    });
  },

  // QUESTION ACTIONS
  addQuestion: (sectionId, type = "short_text") => {
    set((state) => {
      const newItem =
        type === "section"
          ? {
              id: generateId(),
              type: "section",
              title: "New section",
              description: "",
              repeatSection: false,
              items: [],
            }
          : {
              id: generateId(),
              type: "question",
              question: {
                id: generateId(),
                label: "Untitled Question",
                type,
                required: false,
                multipleSelection: false,
                flaggedResponses: false,
                linkResponse: false,
                logicEnabled: false,
              },
            };

      const updated = addItemToSection(state.sections, sectionId, newItem);

      return { ...pushToHistory(state), sections: updated };
    });
  },

  // Structural Section row helper:
  // Creates a new "section" row in the given page (sectionId) so we can
  // treat Section as a structural grouping row (not a regular answer type).
  addSectionRow: (sectionId) => {
    const { addQuestion } = get();
    if (!sectionId) return;
    addQuestion(sectionId, "section");
  },

  duplicateQuestion: (sectionId, questionId) => {
    set((state) => {
      const updated = state.sections.map((sec) => ({
        ...sec,
        items: duplicateQuestionInTree(sec.items || [], questionId),
      }));
      return { ...pushToHistory(state), sections: updated };
    });
  },

  updateQuestion: (sectionId, questionId, changes) => {
    set((state) => {
      const updated = state.sections.map((sec) => ({
        ...sec,
        items: updateQuestionInTree(sec.items || [], questionId, changes),
      }));
      return { ...pushToHistory(state), sections: updated };
    });
  },

  deleteQuestion: (sectionId, questionId) => {
    set((state) => {
      const updated = state.sections.map((sec) => ({
        ...sec,
        items: deleteQuestionInTree(sec.items || [], questionId),
      }));
      return { ...pushToHistory(state), sections: updated };
    });
  },

  // Add Question BELOW any existing row (SafetyCulture-style)
  insertQuestionBelow: (rowId, type = "short_text") => {
    const state = get();
    const flat = flattenTree(state.sections);
    const idx = flat.findIndex((n) => n.id === String(rowId));
    if (idx === -1) return;

    const parentId = flat[idx].parentId;
    const parentItems = parentId ? state.getSectionById(parentId)?.items : state.sections;
    const indexInParent = parentItems
      ? parentItems.findIndex((x) => x.id === flat[idx].id)
      : -1;

    const newItem = {
      id: generateId(),
      type: "question",
      question: {
        id: generateId(),
        label: "Untitled Question",
        type,
        required: false,
        multipleSelection: false,
        flaggedResponses: false,
        linkResponse: false,
        logicEnabled: false,
      },
    };

    const updated = insertAfterIndex(state.sections, parentId, indexInParent, newItem);

    set({
      ...pushToHistory(state),
      sections: updated,
      activeRowId: String(newItem.id),
      activeSectionId: null,
      selectedItemIds: [String(newItem.id)],
    });
  },

  // Add Section BELOW any existing row (auto child question)
  insertSectionBelow: (rowId) => {
    const state = get();
    const flat = flattenTree(state.sections);
    const idx = flat.findIndex((n) => n.id === String(rowId));
    if (idx === -1) return;

    const parentId = flat[idx].parentId;
    const parentItems = parentId ? state.getSectionById(parentId)?.items : state.sections;
    const indexInParent = parentItems
      ? parentItems.findIndex((x) => x.id === flat[idx].id)
      : -1;

    const newSectionId = generateId();
    const newQuestionId = generateId();

    const childQ = {
      id: newQuestionId,
      type: "question",
      question: {
        id: generateId(),
        label: "Untitled Question",
        type: "short_text",
        required: false,
        multipleSelection: false,
        flaggedResponses: false,
        linkResponse: false,
        logicEnabled: false,
      },
    };

    const newSection = {
      id: newSectionId,
      type: "section",
      title: "",
      description: "",
      repeatSection: false,
      items: [childQ],
    };

    const updated = insertAfterIndex(state.sections, parentId, indexInParent, newSection);

    set({
      ...pushToHistory(state),
      sections: updated,
      activeSectionId: String(newSectionId),
      activeRowId: null,
      selectedItemIds: [String(newSectionId), String(newQuestionId)],
    });
  },

  reorderQuestions: (sectionId, newItems) => {
    set((state) => {
      const updated = state.sections.map((sec) => ({
        ...sec,
        items: reorderItemsInTree(sec.items || [], sectionId, newItems),
      }));
      return { ...pushToHistory(state), sections: updated };
    });
  },

  // Move a question between two sections (pages) – used by DnD
  moveQuestion: (fromSectionId, toSectionId, sourceIndex, destIndex) => {
    set((state) => {
      const updated = state.sections.map((sec) => ({
        ...sec,
        items: moveItemInTree(
          sec.items || [],
          fromSectionId,
          toSectionId,
          sourceIndex,
          destIndex
        ),
      }));
      return { ...pushToHistory(state), sections: updated };
    });
  },

  // UNDO/REDO (snapshots include both meta and sections)
  undo: () => {
    set((state) => {
      if (!state.history.length) return {};
      const previous = state.history[state.history.length - 1]; // { meta, sections }
      const newHistory = state.history.slice(0, -1);
      const snapshotNow = { meta: state.meta, sections: state.sections };
      return {
        ...state,
        meta: previous.meta,
        sections: previous.sections,
        history: newHistory,
        future: [snapshotNow, ...state.future],
      };
    });
  },

  redo: () => {
    set((state) => {
      if (!state.future.length) return {};
      const [next, ...future] = state.future; // { meta, sections }
      const snapshotNow = { meta: state.meta, sections: state.sections };
      return {
        ...state,
        meta: next.meta,
        sections: next.sections,
        history: [...state.history, snapshotNow],
        future,
      };
    });
  },

  // ===== MULTIPLE CHOICE RESPONSE SETS (Template & Global) =====
  templateSets: [
    { id: "tpl-yes-no-na", name: "Yes / No / N/A", options: ["Yes", "No", "N/A"] },
  ],
  globalSets: [
    { id: "g-pass-fail-na", name: "Pass / Fail / N/A", options: ["Pass", "Fail", "N/A"] },
    { id: "g-safe-risk", name: "Safe / At Risk / N/A", options: ["Safe", "At Risk", "N/A"] },
  ],

  addTemplateSet: (name = "New Template Set") =>
    set((state) => ({
      templateSets: [
        ...state.templateSets,
        { id: `tpl-${Date.now()}`, name, options: ["Option 1", "Option 2"] },
      ],
    })),
  updateTemplateSet: (id, payload) =>
    set((state) => ({
      templateSets: state.templateSets.map((s) =>
        s.id === id ? { ...s, ...payload } : s
      ),
    })),
  deleteTemplateSet: (id) =>
    set((state) => ({
      templateSets: state.templateSets.filter((s) => s.id !== id),
    })),

  addGlobalSet: (name = "New Global Set") =>
    set((state) => ({
      globalSets: [
        ...state.globalSets,
        { id: `g-${Date.now()}`, name, options: ["Option 1", "Option 2"] },
      ],
    })),
  updateGlobalSet: (id, payload) =>
    set((state) => ({
      globalSets: state.globalSets.map((s) =>
        s.id === id ? { ...s, ...payload } : s
      ),
    })),
  deleteGlobalSet: (id) =>
    set((state) => ({
      globalSets: state.globalSets.filter((s) => s.id !== id),
    })),

  // Allow TemplateBuilder to hydrate sets from API payload
  setTemplateSets: (sets) =>
    set((state) => ({
      templateSets: Array.isArray(sets) ? sets : state.templateSets,
    })),
  setGlobalSets: (sets) =>
    set((state) => ({
      globalSets: Array.isArray(sets) ? sets : state.globalSets,
    })),

  // Resolve options for a question (Custom > Template Set > Global Set > Fallback)
  resolveOptionsForQuestion: (q) => {
    if (Array.isArray(q?.options) && q.options.length) return q.options;
    const ref = q?.responseSet;
    if (ref && ref.type && ref.id) {
      const source =
        ref.type === "template"
          ? get().templateSets
          : ref.type === "global"
          ? get().globalSets
          : [];
      const found = source.find((s) => s.id === ref.id);
      if (found) return found.options || [];
    }
    return ["Option 1", "Option 2"];
  },
}));

// Helper to push the current state to the history stack for undo/redo
function pushToHistory(state) {
  return {
    history: [...state.history, { meta: state.meta, sections: state.sections }],
    future: [],
  };
}
